/** Reaction set shared by feed posts, comments and messages (PRODUCT.md › Feed). */
export const REACTION_TYPES = ['like', 'love', 'laugh', 'wow', 'sad', 'angry'] as const;
export type ReactionType = (typeof REACTION_TYPES)[number];

export const REACTION_EMOJI: Record<ReactionType, string> = {
  like: '👍',
  love: '❤️',
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😡',
};

export interface ReactionSummary {
  type: ReactionType;
  count: number;
  reactedByMe: boolean;
}

/**
 * Optimistically applies a toggle: removes my previous reaction (one reaction per user) and adds `type`
 * unless it was already mine. Useful in `onMutate` of reaction mutations.
 */
export function toggleReaction(reactions: readonly ReactionSummary[], type: ReactionType): ReactionSummary[] {
  const mine = reactions.find((r) => r.reactedByMe);
  const removing = mine?.type === type;
  const next = reactions
    .map((r) => (r.reactedByMe ? { ...r, count: r.count - 1, reactedByMe: false } : r))
    .filter((r) => r.count > 0);
  if (removing) return next;
  const existing = next.find((r) => r.type === type);
  if (existing)
    return next.map((r) => (r.type === type ? { ...r, count: r.count + 1, reactedByMe: true } : r));
  return [...next, { type, count: 1, reactedByMe: true }].sort(
    (a, b) => REACTION_TYPES.indexOf(a.type) - REACTION_TYPES.indexOf(b.type),
  );
}
